/**
 * Acquired Book Performance Section
 *
 * Displays performance metrics for acquired loan books:
 * - How many loans have discharged from the acquired book
 * - How many were retained (refinanced into primary book)
 * - Monthly and yearly trends
 */

import React, { useState, useEffect } from 'react';
import { BookOpen, TrendingUp, TrendingDown, AlertTriangle, RefreshCw, ChevronDown, ChevronUp, Share2, Check, Copy, X, Link, Loader2, Calendar, BarChart3, LineChart, DollarSign, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import toast from 'react-hot-toast';
import { useAcquiredBookMetrics, AcquiredBookMetrics } from '../../hooks/useAcquiredBookMetrics';
import { formatCompactCurrency, formatCurrency } from '../../utils/formatters';
import RetentionTrendChart from './RetentionTrendChart';

/**
 * Format YYYY-MM to "MMM YY" (e.g., "2023-04" → "Apr 23")
 * Note: Rolling 3-run month ranges are now pre-formatted by the backend
 */
const formatMonthShort = (yyyyMm: string): string => {
  if (!yyyyMm || yyyyMm.length < 7) return yyyyMm;
  // If already formatted (contains letters), return as-is
  if (/[a-zA-Z]/.test(yyyyMm)) return yyyyMm;
  const [year, month] = yyyyMm.split('-');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthIndex = parseInt(month, 10) - 1;
  const shortYear = year.slice(2);
  return `${monthNames[monthIndex] || month} ${shortYear}`;
};

/** Compact currency from cents, the app-wide three-significant-figure rule. */
const formatCurrencyCompact = (cents: number): string => formatCompactCurrency(cents);

// Share Modal for link sharing
interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareUrl: string;
  expiresAt?: string;
  isLoading: boolean;
  error?: string | null;
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, shareUrl, expiresAt, isLoading, error }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const formatExpirationDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-2">
            <Link className="w-5 h-5 text-purple-400" />
            <h3 className="text-lg font-bold text-white">Share Acquired Book Report</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading ? (
          <div className="py-8 flex flex-col items-center justify-center">
            <Loader2 className="w-8 h-8 text-purple-400 animate-spin mb-3" />
            <p className="text-gray-400">Generating share link...</p>
          </div>
        ) : error ? (
          <div className="py-4">
            <div className="p-4 bg-red-900/30 border border-red-700 rounded-lg text-red-300">
              <p>Failed to generate share link: {error}</p>
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-700 rounded-md hover:bg-gray-600 transition-colors text-white"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-gray-300 mb-4">
              Anyone with this link can view this acquired book performance report. No login required.
            </p>

            <div className="flex items-center gap-2 mb-4">
              <input
                type="text"
                readOnly
                value={shareUrl}
                className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm font-mono"
              />
              <button
                onClick={handleCopy}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white flex items-center gap-2 transition-colors"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>

            {expiresAt && (
              <div className="flex items-center gap-2 mb-6 text-sm text-gray-400">
                <Calendar className="w-4 h-4" />
                <span>Link expires: {formatExpirationDate(expiresAt)}</span>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-700 rounded-md hover:bg-gray-600 transition-colors text-white"
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

interface FileTemplate {
  id: string;
  templateName: string;
  isAcquiredLoanBook: boolean;
}

interface AcquiredBookPerformanceSectionProps {
  companyId: string;
  primaryTemplate: FileTemplate | null;
  acquiredTemplates: FileTemplate[];
}

/**
 * Metric card component for summary stats
 */
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  subValue?: string;
  icon: React.ReactNode;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
}> = ({ title, value, subValue, icon, color, trend }) => (
  <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-2">
        <div className={`p-2 rounded-lg ${color}`}>
          {icon}
        </div>
        <span className="text-gray-400 text-sm">{title}</span>
      </div>
      {trend && (
        <div className={`flex items-center ${trend === 'up' ? 'text-green-400' : trend === 'down' ? 'text-red-400' : 'text-gray-400'}`}>
          {trend === 'up' ? <TrendingUp className="w-4 h-4" /> : trend === 'down' ? <TrendingDown className="w-4 h-4" /> : null}
        </div>
      )}
    </div>
    <div className="mt-3">
      <p className="text-2xl font-bold text-white">{value}</p>
      {subValue && <p className="text-sm text-gray-400 mt-1">{subValue}</p>}
    </div>
  </div>
);

const AcquiredBookPerformanceSection: React.FC<AcquiredBookPerformanceSectionProps> = ({
  companyId,
  primaryTemplate,
  acquiredTemplates,
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [showSection, setShowSection] = useState(true);
  const [chartView, setChartView] = useState<'combined' | 'simple'>('combined');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareExpiresAt, setShareExpiresAt] = useState<string | undefined>();
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const { metrics, isLoading, error, fetchMetrics, clearMetrics } = useAcquiredBookMetrics();

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
  };

  const handleShare = async () => {
    if (!selectedTemplateId || !primaryTemplate) return;

    setShareModalOpen(true);
    setShareLoading(true);
    setShareError(null);

    try {
      const generateToken = httpsCallable<
        { companyId: string; acquiredTemplateId: string; primaryTemplateId: string; expiresInDays?: number },
        { token: string; shareUrl: string; expiresAt?: string }
      >(functions, 'generateAcquiredBookShareToken');

      const result = await generateToken({
        companyId,
        acquiredTemplateId: selectedTemplateId,
        primaryTemplateId: primaryTemplate.id,
        expiresInDays: 30,
      });

      setShareUrl(result.data.shareUrl);
      setShareExpiresAt(result.data.expiresAt);
    } catch (err: any) {
      console.error('Failed to generate share token:', err);
      setShareError(err.message || 'Failed to generate share link');
    } finally {
      setShareLoading(false);
    }
  };

  // Auto-select first acquired template if only one exists
  useEffect(() => {
    if (acquiredTemplates.length === 1 && !selectedTemplateId) {
      setSelectedTemplateId(acquiredTemplates[0].id);
    }
  }, [acquiredTemplates, selectedTemplateId]);

  // Fetch metrics when selection changes
  useEffect(() => {
    if (selectedTemplateId && companyId && primaryTemplate) {
      fetchMetrics({
        companyId,
        acquiredTemplateId: selectedTemplateId,
        primaryTemplateId: primaryTemplate.id,
      });
    } else {
      clearMetrics();
    }
  }, [selectedTemplateId, companyId, primaryTemplate, fetchMetrics, clearMetrics]);

  const handleRefresh = () => {
    if (selectedTemplateId && primaryTemplate) {
      fetchMetrics({
        companyId,
        acquiredTemplateId: selectedTemplateId,
        primaryTemplateId: primaryTemplate.id,
      });
    }
  };

  // Don't render if no acquired templates configured
  if (acquiredTemplates.length === 0) {
    return null;
  }

  // Don't render if no primary template configured
  if (!primaryTemplate) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <div className="flex items-center gap-3 text-yellow-400">
          <AlertTriangle className="w-5 h-5" />
          <p>Please configure a primary loan book above to track acquired book performance.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
      {/* Section Header */}
      <button
        onClick={() => setShowSection(!showSection)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-750 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <BookOpen className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Acquired Book Performance</h2>
            <p className="text-sm text-gray-400">
              Track how acquired loan books perform over time
            </p>
          </div>
        </div>
        {showSection ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {showSection && (
        <div className="px-6 pb-6 border-t border-gray-700">
          {/* Controls Row */}
          <div className="flex flex-wrap items-center gap-4 mt-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm text-gray-400 mb-1">Select Acquired Book</label>
              <select
                value={selectedTemplateId}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-purple-500"
              >
                <option value="">-- Select an acquired book --</option>
                {acquiredTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.templateName}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-400">
              <span>Comparing to:</span>
              <span className="text-green-400 font-medium">{primaryTemplate.templateName}</span>
              <span>(Primary)</span>
            </div>

            {selectedTemplateId && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleRefresh}
                  disabled={isLoading}
                  className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <button
                  onClick={handleShare}
                  className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm flex items-center gap-2 transition-colors"
                >
                  <Share2 className="w-4 h-4" />
                  Share Link
                </button>
              </div>
            )}
          </div>

          {/* Error State */}
          {error && (
            <div className="mt-4 p-4 bg-red-900/20 border border-red-700 rounded-lg text-red-300">
              <p>Failed to load metrics: {error.message}</p>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="mt-6 flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
              <span className="ml-3 text-gray-400">Loading metrics...</span>
            </div>
          )}

          {/* No Selection State */}
          {!selectedTemplateId && !isLoading && (
            <div className="mt-6 py-12 text-center text-gray-400">
              <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Select an acquired book above to view performance metrics</p>
            </div>
          )}

          {/* Metrics Display */}
          {metrics && !isLoading && (
            <div className="mt-6 space-y-6">
              {/* Summary Cards - Row 1: Counts */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard
                  title="Total Discharged"
                  value={metrics.totalDischargesFromBook.toLocaleString()}
                  subValue="loans from acquired book"
                  icon={<TrendingDown className="w-4 h-4 text-gray-400" />}
                  color="bg-gray-700"
                />
                <MetricCard
                  title="Retained to Primary"
                  value={`${metrics.retainedConfirmed} ✓`}
                  subValue={metrics.retainedPending > 0 ? `+${metrics.retainedPending} pending verification` : 'all verified'}
                  icon={<TrendingUp className="w-4 h-4 text-green-400" />}
                  color="bg-green-900/30"
                  trend="up"
                />
                <MetricCard
                  title="Lost to External"
                  value={metrics.lostToExternal.toLocaleString()}
                  subValue="loans not retained"
                  icon={<AlertTriangle className="w-4 h-4 text-red-400" />}
                  color="bg-red-900/30"
                  trend="down"
                />
                <MetricCard
                  title="Retention Rate"
                  value={`${metrics.retentionRate}%`}
                  subValue={`${metrics.matchedDischargedLoans || 0} of ${metrics.totalDischargesFromBook} discharges refinanced → ${metrics.retainedToPrimary} new loans`}
                  icon={<TrendingUp className="w-4 h-4 text-blue-400" />}
                  color="bg-blue-900/30"
                  trend={metrics.retentionRate >= 50 ? 'up' : metrics.retentionRate >= 25 ? 'neutral' : 'down'}
                />
              </div>

              {/* WS1: "retained" now counts confirmed refinances only */}
              <p className="-mt-4 text-xs text-gray-500">
                Retained = confirmed refinances from the acquired book into your primary book. Pending
                items are matches awaiting verification and are not yet counted as retained.
              </p>

              {/* Summary Cards - Row 2: Loan Value Metrics + Trail Book Health */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard
                  title="Total Discharged Value"
                  value={formatCurrency(metrics.dischargedValueCents)}
                  subValue="loan value at discharge"
                  icon={<DollarSign className="w-4 h-4 text-gray-400" />}
                  color="bg-gray-700"
                />
                <MetricCard
                  title="Total Refinanced Value"
                  value={formatCurrency(metrics.retainedLoanAmountCents)}
                  subValue="new loan values in primary book"
                  icon={<DollarSign className="w-4 h-4 text-green-400" />}
                  color="bg-green-900/30"
                  trend="up"
                />
                {(() => {
                  const loanValueGain = metrics.retainedLoanAmountCents - metrics.dischargedValueCents;
                  const percentChange = metrics.dischargedValueCents > 0
                    ? ((loanValueGain / metrics.dischargedValueCents) * 100).toFixed(1)
                    : 0;
                  const isPositive = loanValueGain >= 0;
                  return (
                    <MetricCard
                      title="Loan Value Gain"
                      value={`${isPositive ? '+' : ''}${formatCurrency(loanValueGain)}`}
                      subValue={`${isPositive ? '+' : ''}${percentChange}% vs discharged`}
                      icon={isPositive ? <ArrowUpRight className="w-4 h-4 text-green-400" /> : <ArrowDownRight className="w-4 h-4 text-red-400" />}
                      color={isPositive ? 'bg-green-900/30' : 'bg-red-900/30'}
                      trend={isPositive ? 'up' : 'down'}
                    />
                  );
                })()}
                {/* Trail Book Health: Rolling 3-run comparison for accurate trend analysis */}
                {metrics.firstRunMonth && metrics.latestRunMonth && metrics.firstRunMonth !== metrics.latestRunMonth ? (
                  (() => {
                    const trailChange = metrics.trailChangePercent;
                    const isPositive = trailChange > 0;
                    const isNeutral = trailChange === 0;
                    const isNegative = trailChange < 0;
                    const firstAmount = formatCurrencyCompact(metrics.firstRunTrailCents);
                    const latestAmount = formatCurrencyCompact(metrics.latestRunTrailCents);
                    return (
                      <MetricCard
                        title="Trail Book Change (3-run)"
                        value={`${isPositive ? '+' : ''}${trailChange}%`}
                        subValue={`${metrics.firstRunMonth} ${firstAmount} → ${metrics.latestRunMonth} ${latestAmount}`}
                        icon={
                          isNeutral
                            ? <Minus className="w-4 h-4 text-gray-400" />
                            : isNegative
                              ? <TrendingDown className="w-4 h-4 text-red-400" />
                              : <TrendingUp className="w-4 h-4 text-green-400" />
                        }
                        color={isNeutral ? 'bg-gray-700' : isNegative ? 'bg-red-900/30' : 'bg-green-900/30'}
                        trend={isNeutral ? 'neutral' : isNegative ? 'down' : 'up'}
                      />
                    );
                  })()
                ) : (
                  <MetricCard
                    title="Trail Book Change (3-run)"
                    value="N/A"
                    subValue="Not enough data yet"
                    icon={<Minus className="w-4 h-4 text-gray-400" />}
                    color="bg-gray-700"
                  />
                )}
              </div>

              {/* Summary Cards - Row 3: Commission + Trail Comparison Metrics */}
              {(metrics.previousAvgMonthlyTrailCents > 0 || metrics.newAvgMonthlyTrailCents > 0) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <MetricCard
                    title="Total Upfront Commission"
                    value={formatCurrency(metrics.retainedConfirmedCommissionCents)}
                    subValue={`from ${metrics.retainedConfirmed} retained loans`}
                    icon={<TrendingUp className="w-4 h-4 text-green-400" />}
                    color="bg-green-900/30"
                    trend="up"
                  />
                  <MetricCard
                    title="Previous Avg Monthly Trail"
                    value={formatCurrency(metrics.previousAvgMonthlyTrailCents)}
                    subValue="monthly trail before discharge"
                    icon={<DollarSign className="w-4 h-4 text-purple-400" />}
                    color="bg-purple-900/30"
                  />
                  <MetricCard
                    title="New Avg Monthly Trail"
                    value={formatCurrency(metrics.newAvgMonthlyTrailCents)}
                    subValue="monthly trail after refinance"
                    icon={<DollarSign className="w-4 h-4 text-cyan-400" />}
                    color="bg-cyan-900/30"
                  />
                  {(() => {
                    const trailDelta = metrics.trailDeltaCents;
                    const isPositive = trailDelta >= 0;
                    const isNeutral = trailDelta === 0;
                    return (
                      <MetricCard
                        title="Net Trail Impact"
                        value={`${isPositive && !isNeutral ? '+' : ''}${formatCurrency(trailDelta)}`}
                        subValue={isNeutral ? 'no change' : `${isPositive ? 'gained' : 'reduced'} per month`}
                        icon={
                          isNeutral
                            ? <Minus className="w-4 h-4 text-gray-400" />
                            : isPositive
                              ? <ArrowUpRight className="w-4 h-4 text-green-400" />
                              : <ArrowDownRight className="w-4 h-4 text-red-400" />
                        }
                        color={isNeutral ? 'bg-gray-700' : isPositive ? 'bg-green-900/30' : 'bg-red-900/30'}
                        trend={isNeutral ? 'neutral' : isPositive ? 'up' : 'down'}
                      />
                    );
                  })()}
                </div>
              )}

              {/* Fallback: Show commission card alone if no trail data */}
              {!(metrics.previousAvgMonthlyTrailCents > 0 || metrics.newAvgMonthlyTrailCents > 0) && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <MetricCard
                    title="Total Upfront Commission"
                    value={formatCurrency(metrics.retainedConfirmedCommissionCents)}
                    subValue={`from ${metrics.retainedConfirmed} retained loans`}
                    icon={<TrendingUp className="w-4 h-4 text-green-400" />}
                    color="bg-green-900/30"
                    trend="up"
                  />
                </div>
              )}

              {/* Book Flow Visualization */}
              <div className="bg-gray-900 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-400 mb-3">Loan Book Flow</h3>
                <div className="flex items-center justify-center gap-4 flex-wrap">
                  <div className="bg-purple-900/30 border border-purple-700 rounded-lg px-4 py-3 text-center">
                    <p className="text-purple-300 text-sm">{metrics.acquiredTemplateName}</p>
                    <p className="text-white font-bold">{metrics.totalDischargesFromBook} discharged</p>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="text-green-400 text-sm">{metrics.retentionRate}% retained</div>
                    <div className="w-20 h-0.5 bg-gradient-to-r from-purple-500 to-green-500" />
                    <div className="text-gray-400 text-sm">{100 - metrics.retentionRate}% lost</div>
                  </div>
                  <div className="bg-green-900/30 border border-green-700 rounded-lg px-4 py-3 text-center">
                    <p className="text-green-300 text-sm">{metrics.primaryTemplateName}</p>
                    <p className="text-white font-bold">{metrics.retainedConfirmed} ✓ retained</p>
                    {metrics.retainedPending > 0 && (
                      <p className="text-yellow-400 text-xs">+{metrics.retainedPending} pending</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Monthly Trend Chart */}
              {metrics.monthlyTrends.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-medium text-white">Monthly Retention Trend</h3>
                    <div className="flex items-center gap-1 bg-gray-900 rounded-lg p-1">
                      <button
                        onClick={() => setChartView('combined')}
                        className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${
                          chartView === 'combined'
                            ? 'bg-purple-600 text-white'
                            : 'text-gray-400 hover:text-white hover:bg-gray-800'
                        }`}
                        title="Bar chart with retention rate line"
                      >
                        <BarChart3 className="w-4 h-4" />
                        Detailed
                      </button>
                      <button
                        onClick={() => setChartView('simple')}
                        className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5 ${
                          chartView === 'simple'
                            ? 'bg-purple-600 text-white'
                            : 'text-gray-400 hover:text-white hover:bg-gray-800'
                        }`}
                        title="Stacked area chart"
                      >
                        <LineChart className="w-4 h-4" />
                        Stacked
                      </button>
                    </div>
                  </div>
                  <RetentionTrendChart
                    data={metrics.monthlyTrends}
                    title=""
                    viewMode={chartView}
                  />
                </div>
              )}

              {/* Yearly Summary Table */}
              {metrics.yearlySummary.length > 0 && (
                <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
                  <h3 className="text-lg font-medium text-white px-4 py-3 border-b border-gray-700">
                    Yearly Summary
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-800">
                          <th className="px-4 py-3 text-left text-gray-400 font-medium">Year</th>
                          <th className="px-4 py-3 text-right text-gray-400 font-medium">Discharged</th>
                          <th className="px-4 py-3 text-right text-gray-400 font-medium">Retained (✓/⏳)</th>
                          <th className="px-4 py-3 text-right text-gray-400 font-medium">Lost</th>
                          <th className="px-4 py-3 text-right text-gray-400 font-medium">Rate</th>
                          <th className="px-4 py-3 text-right text-gray-400 font-medium">Upfront Commission</th>
                          <th className="px-4 py-3 text-right text-gray-400 font-medium">Loan Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {metrics.yearlySummary.map((year) => (
                          <tr key={year.year} className="border-t border-gray-800 hover:bg-gray-800/50">
                            <td className="px-4 py-3 text-white font-medium">{year.year}</td>
                            <td className="px-4 py-3 text-right text-gray-300">{year.discharges.toLocaleString()}</td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-green-400">{year.retainedConfirmed}✓</span>
                              {year.retainedPending > 0 && (
                                <span className="text-yellow-400 ml-1">+{year.retainedPending}⏳</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right text-red-400">{year.lost.toLocaleString()}</td>
                            <td className="px-4 py-3 text-right">
                              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                year.retentionRate >= 50 ? 'bg-green-900/50 text-green-300' :
                                year.retentionRate >= 25 ? 'bg-yellow-900/50 text-yellow-300' :
                                'bg-red-900/50 text-red-300'
                              }`}>
                                {year.retentionRate}%
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-green-400">
                              {formatCurrency(year.retainedCommissionCents)}
                            </td>
                            <td className="px-4 py-3 text-right text-cyan-400">
                              {formatCurrency(year.retainedLoanAmountCents)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        {(() => {
                          // Use pre-computed metrics totals instead of summing yearly data
                          // Yearly sums can double-count loans that appear in multiple years
                          const totals = {
                            discharges: metrics.totalDischargesFromBook,
                            retainedConfirmed: metrics.retainedConfirmed,
                            retainedPending: metrics.retainedPending,
                            lost: metrics.lostToExternal,
                            commissionCents: metrics.retainedCommissionCents,
                            loanValueCents: metrics.retainedLoanAmountCents,
                          };
                          const totalRetained = totals.retainedConfirmed + totals.retainedPending;
                          const overallRate = metrics.retentionRate;
                          return (
                            <tr className="border-t-2 border-gray-700 bg-gray-800 font-semibold">
                              <td className="px-4 py-3 text-white">Total</td>
                              <td className="px-4 py-3 text-right text-gray-200">{totals.discharges.toLocaleString()}</td>
                              <td className="px-4 py-3 text-right">
                                <span className="text-green-400">{totals.retainedConfirmed}✓</span>
                                {totals.retainedPending > 0 && (
                                  <span className="text-yellow-400 ml-1">+{totals.retainedPending}⏳</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right text-red-400">{totals.lost.toLocaleString()}</td>
                              <td className="px-4 py-3 text-right">
                                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                  overallRate >= 50 ? 'bg-green-900/50 text-green-300' :
                                  overallRate >= 25 ? 'bg-yellow-900/50 text-yellow-300' :
                                  'bg-red-900/50 text-red-300'
                                }`}>
                                  {overallRate}%
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right text-green-400">
                                {formatCurrency(totals.commissionCents)}
                              </td>
                              <td className="px-4 py-3 text-right text-cyan-400">
                                {formatCurrency(totals.loanValueCents)}
                              </td>
                            </tr>
                          );
                        })()}
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Empty state for no data */}
              {metrics.totalDischargesFromBook === 0 && (
                <div className="text-center py-8 text-gray-400">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No discharge data found for this acquired book.</p>
                  <p className="text-sm mt-1">
                    This could mean no loans have been discharged yet, or the data hasn't been uploaded.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Share Modal */}
      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        shareUrl={shareUrl}
        expiresAt={shareExpiresAt}
        isLoading={shareLoading}
        error={shareError}
      />
    </div>
  );
};

export default AcquiredBookPerformanceSection;
